import {importProvidersFrom} from '@angular/core';
import {Routes} from '@angular/router';
import {canActivateAuthRole, canActivateGroupEntrepriseAuthRole} from '@auth/index';
import {StoreModule} from '@ngrx/store';
import {actualitiesReducer} from '@features/dashboard/mes-actualites/stores/actualities.reducer';
import {EffectsModule} from '@ngrx/effects';
import {ActualitiesEffects} from '@features/dashboard/mes-actualites/stores/actualities.effects';
import {
  folderSummaryFeatureKey,
  folderSummaryReducer,
} from '@features/dashboard/mon-stock/store/features/folder.feature';
import {FolderSummaryEffects} from '@features/dashboard/mon-stock/store/effects/folder.effects';
import {mesFinancementsRechercheReducers} from '@features/mes-financements/store/search/search.reducers';
import {mesFinancementsFeature} from '@features/mes-financements/store/financements/mes-financements.reducers';
import * as searchEffects from '@features/mes-financements/store/search/search.effects';
import * as mesRemboursementsSearchEffects from '@features/mes-remboursements/store/search/search.effects';
import * as mesDossiersSearchEffects from '@features/demande-remboursement/choix-dossier/store/search/search.effects';
import {mesDossiersRechercheReducers} from '@features/demande-remboursement/choix-dossier/store/search/search.reducers';
import {
  mesDossiersFeature
} from '@features/demande-remboursement/choix-dossier/store/mes-dossiers/mes-dossiers.reducers';
import {
  MesEtablissementSearchFormReducers
} from '@features/compte/mes-etablissements/features/mes-etablissements-table/search/search.reducer';
import {
  MonEtablissementReducer
} from '@features/compte/mon-etablissement/features/mon-etablissement-detail/store/reducers/mon-etablissement.reducer';
import {
  MonEtablissementEffects
} from '@features/compte/mon-etablissement/features/mon-etablissement-detail/store/effects/mon-etablissement.effects';
import {mesRemboursementsFeature} from '@features/mes-remboursements/store/main.reducer';

export const AppRoutes: Routes = [
  {
    path: '',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/accueil/accueil.component').then((m) => m.PageAccueilComponent),
      },
      {
        path: '',
        children: [
          {
            path: 'cgu',
            loadComponent: () =>
              import('./pages/legal/cgu/cgu.component').then((m) => m.PageCguComponent),
          },
          {
            path: 'rgpd',
            loadComponent: () =>
              import('./pages/legal/rgpd/rgpd.component').then((m) => m.PageRgpdComponent),
          },
          {
            path: 'cookies',
            loadComponent: () =>
              import('./pages/legal/cookies/cookies.component').then((m) => m.PageCookiesComponent),
          },
          {
            path: 'autres-portails',
            loadComponent: () =>
              import('./pages/legal/autres-portails/autres-portails.component').then(
                (m) => m.PageAutresPortailsComponent,
              ),
          },
          {
            path: 'politique-de-confidentialite',
            loadComponent: () =>
              import(
                './pages/legal/politique-de-confidentialite/politique-de-confidentialite.component'
              ).then((m) => m.PagePolitiqueDeConfidentialiteComponent),
          },
          {
            path: 'cggc',
            loadComponent: () =>
              import(
                './pages/legal/cggc/cggc.component'
              ).then((m) => m.PageCggcComponent),
          },
          {
            path: 'mentions-legales',
            loadComponent: () =>
              import('./pages/legal/mentions-legales/mentions-legales.component').then(
                (m) => m.PageMentionsLegalesComponent,
              ),
          },
        ],
      },
      {
        path: '',
        canActivateChild: [canActivateGroupEntrepriseAuthRole],
        children: [
          {
            path: 'mon-tableau-de-bord',
            providers: [
              importProvidersFrom(
                StoreModule.forFeature({ name: 'actualities', reducer: actualitiesReducer }),
                StoreModule.forFeature(folderSummaryFeatureKey, folderSummaryReducer),
                StoreModule.forFeature({
                  name: 'mesFinancementsRechercheForm',
                  reducer: mesFinancementsRechercheReducers,
                }),
                StoreModule.forFeature(mesFinancementsFeature),
                StoreModule.forFeature({
                  name: 'mesDossiersRechercheForm',
                  reducer: mesDossiersRechercheReducers,
                }),
                StoreModule.forFeature(mesDossiersFeature),
                StoreModule.forFeature(mesRemboursementsFeature),
                EffectsModule.forFeature([
                  ActualitiesEffects,
                  FolderSummaryEffects,
                  searchEffects,
                  mesDossiersSearchEffects,
                  mesRemboursementsSearchEffects
                ]),
              ),
            ],
            canActivate: [canActivateAuthRole],
            loadComponent: () =>
              import('./pages/dashboard/dashboard.component').then((m) => m.PageDashboardComponent),
          },
          {
            path: 'demande-financement',
            loadChildren: () =>
              import('./pages/demande-financement/demande-financement.routing').then(
                (m) => m.MenuRoutes,
              ),
          },
          {
            path: 'demande-un-remboursement',
            loadChildren: () =>
              import('./pages/demande-remboursement/demande-remboursement.routing').then(
                (m) => m.MenuRoutes,
              ),
          },
          {
            path: 'editiques',
            loadChildren: () =>
              import('./pages/editiques/editiques.routing').then((m) => m.EditiquesRoutes),
          },
          {
            path: 'comment-ca-marche',
            canActivate: [canActivateAuthRole],
            loadComponent: () =>
              import('./pages/comment-ca-marche/comment-ca-marche.component').then(
                (m) => m.PageCommentCaMarcheComponent,
              ),
          },
          {
            path: 'faq',
            canActivate: [canActivateAuthRole],
            loadComponent: () => import('./pages/faq/faq.component').then((m) => m.PageFaqComponent),
          },
          {
            path: 'mes-coordonnees',
            canActivate: [canActivateAuthRole],
            loadComponent: () =>
              import('./pages/compte/mes-coordonnees/mes-coordonnees.component').then(
                (m) => m.PageMesCoordonneesComponent,
              ),
          },
          {
            path: 'mes-etablissements',
            canActivate: [canActivateAuthRole],
            providers: [
              importProvidersFrom(
                StoreModule.forFeature({
                  name: 'mesEtablissementsSearcheForm',
                  reducer: MesEtablissementSearchFormReducers,
                }),
              ),
            ],
            loadComponent: () =>
              import('./pages/compte/mes-etablissements/mes-etablissements.component').then(
                (m) => m.PageMesEtablissementsComponent,
              ),
          },
          {
            path: 'mon-etablissement/:siretEtablissement',
            canActivate: [canActivateAuthRole],
            providers: [
              importProvidersFrom(
                StoreModule.forFeature({
                  name: 'monEtablissementForm',
                  reducer: MonEtablissementReducer,
                }),
                EffectsModule.forFeature(MonEtablissementEffects),
              ),
            ],
            loadComponent: () =>
              import('./pages/compte/mon-etablissement/mon-etablissement.component').then(
                (m) => m.PageMonEtablissementComponent,
              ),
          },
          {
            path: 'mes-financements',
            canActivate: [canActivateAuthRole],
            loadChildren: () =>
              import('./pages/mes-financements/mes-financements.routing').then(
                (m) => m.MesFinancementsRoutes,
              ),
          },
          {
            path: 'mes-remboursements',
            canActivate: [canActivateAuthRole],
            loadChildren: () =>
              import('./pages/mes-remboursements/mes-remboursements.routing').then(
                (m) => m.MesRemboursementsRoutes,
              ),
          },
          {
            path: 'mes-avis-virements',
            canActivate: [canActivateAuthRole],
            loadChildren: () =>
              import('./pages/mes-avis-virements/mes-avis-virements.routing').then(
                (m) => m.MesAvisVirementsRoutes,
              ),
          },
        ]
      },
    ],
  },
  { path: '**', redirectTo: '', pathMatch: 'full' },
];
